import React, { useState } from 'react';
import { FiMail, FiPhone, FiMapPin, FiClock, FiMessageCircle, FiSend, FiUser, FiCheck } from 'react-icons/fi';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://shop.bailebelle.com/wp-json/bailebelle/v1/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          phone: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <SEO
          title="Contact Us | BaileBelle - Premium Ballerina Dresses & Jewelry"
          description="Get in touch with BaileBelle for questions about our ballerina dresses, jewelry, or dance accessories. We're here to help you find the perfect dance wear."
          keywords="contact BaileBelle, customer service, ballerina dress help, dance wear support, customer support"
          url="/contact"
        />
        <div className="min-h-screen bg-black text-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="bg-green-500/20 border border-green-500/30 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-8">
                <FiCheck className="text-green-500 w-10 h-10" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'serif' }}>
                Message Sent Successfully!
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
                Thank you for reaching out to us. We've received your message and will get back to you within 12-24 hours.
              </p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="px-8 py-4 bg-pink-500 text-black font-bold rounded-xl hover:bg-pink-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-pink-500/30"
              >
                Send Another Message
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Contact Us | BaileBelle - Premium Ballerina Dresses & Jewelry"
        description="Get in touch with BaileBelle for questions about our ballerina dresses, jewelry, or accessories. We're here to help you find the perfect dress."
        keywords="contact BaileBelle, customer service, ballerina dress help, dress support, customer support"
        url="/contact"
      />
      <div className="min-h-screen bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
              Contact Us
            </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Have a question about our products or need assistance? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6">Contact Information</h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-pink-500/10 p-3 rounded-lg">
                      <FiMapPin className="text-pink-500 w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Address</p>
                      <p className="text-gray-400 text-sm mt-1">
                        SKYCONSULTANTS PRIVATE LIMITED<br />
                        BAILEBELLE FASHION<br /><br />
                        <strong>Address:</strong><br /><br />
                        <strong>PK:</strong> KARIM ABAD KHAN GHARI Dargai, Malakand KPK<br />
                        <strong>US:</strong> 100 N Howard ST Spokane, Washington 99201
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-pink-500/10 p-3 rounded-lg">
                      <FiPhone className="text-pink-500 w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Phone</p>
                      <p className="text-gray-400 text-sm mt-1">0336-8886865</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-pink-500/10 p-3 rounded-lg">
                      <FiMail className="text-pink-500 w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Email</p>
                      <p className="text-gray-400 text-sm mt-1">supports@bailebelle.com</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-pink-500/10 p-3 rounded-lg">
                      <FiClock className="text-pink-500 w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Business Hours</p>
                      <p className="text-gray-400 text-sm mt-1">Available 24/7</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-white mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors"
                      placeholder="What's this about?"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-white mb-2">
                    Message *
                  </label>
                  <div className="relative">
                    <FiMessageCircle className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors resize-none"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-8 py-4 bg-pink-500 text-black font-bold rounded-xl hover:bg-pink-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-pink-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-3"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiSend className="mr-3 w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-12 pt-8 border-t border-gray-800">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-white mb-4">Business Hours</h3>
                  <div className="flex items-center justify-center space-x-2 text-gray-400 mb-2">
                    <FiClock className="w-4 h-4" />
                    <span>24/7</span>
                  </div>
                  <p className="text-gray-500 text-sm mt-4">
                    We typically respond within 12-24 hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
